/**
 * @voltxampere/engine — Assertion DSL builtin measurements (Sprint 9)
 *
 * `Ipeak`, `Vpeak`, `Irms`, `Vrms` reduce a transient waveform or
 * AC magnitude sweep to a single scalar the evaluator can compare
 * against a bound. Sprint 9 ships exactly those four. Sprint 10+
 * will extend the dictionary with freq / period / duty / risetime
 * once the AC sweep lands.
 *
 * All builtins are pure functions of the snapshot data available
 * at evaluation time. DC analyses hold a single operating point,
 * so `Ipeak(D1)` on a DC result returns the absolute value of the
 * DC current — consistent with the ngspice convention of treating
 * a constant signal as its own peak.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export const BUILTIN_NAMES = ["Ipeak", "Vpeak", "Irms", "Vrms"];
/** Kind of signal the builtin consumes: `"current"` or `"voltage"`. */
function signalKindOf(name) {
    return name === "Ipeak" || name === "Irms" ? "current" : "voltage";
}
/**
 * Compute a builtin over a transient result. `target` is the
 * name of the net (for voltage) or component (for current).
 *
 * Throws {@link RangeError} if the target is not found — the
 * evaluator catches that and converts it to an i18n'd message
 * with a "did you mean" suggestion where applicable.
 */
export function computeBuiltinTransient(name, target, transient) {
    const samples = extractTransientSamples(name, target, transient);
    if (name === "Ipeak" || name === "Vpeak")
        return peak(samples);
    return rms(samples);
}
/**
 * DC operating-point version — returns the signed value of the
 * requested node voltage or branch current. For `Ipeak`/`Vpeak`
 * we return `|value|`; for `Irms`/`Vrms` we return `|value|` too
 * (a constant signal equals its own RMS).
 */
export function computeBuiltinDC(name, target, dc) {
    const kind = signalKindOf(name);
    let value;
    if (kind === "voltage") {
        const v = dc.nodeVoltages.get(target);
        if (v === undefined) {
            throw new RangeError(`node '${target}' not present in DC result`);
        }
        value = v;
    }
    else {
        const i = dc.sourceCurrents.get(target);
        if (i === undefined) {
            throw new RangeError(`component '${target}' has no DC branch current`);
        }
        value = i;
    }
    return Math.abs(value);
}
function extractTransientSamples(name, target, transient) {
    const kind = signalKindOf(name);
    const points = transient.timeSteps;
    if (points.length === 0) {
        throw new RangeError("transient result has no time steps");
    }
    const out = [];
    const key = target;
    for (const p of points) {
        const src = kind === "voltage" ? p.nodeVoltages : p.sourceCurrents;
        const v = src.get(key);
        if (v === undefined) {
            throw new RangeError(`${kind === "voltage" ? "node" : "component"} '${target}' not present in transient result`);
        }
        out.push(v);
    }
    return out;
}
function peak(samples) {
    let m = 0;
    for (const v of samples) {
        const a = Math.abs(v);
        if (a > m)
            m = a;
    }
    return m;
}
function rms(samples) {
    if (samples.length === 0)
        return 0;
    let sum = 0;
    for (const v of samples)
        sum += v * v;
    return Math.sqrt(sum / samples.length);
}
export function isBuiltinName(name) {
    return BUILTIN_NAMES.includes(name);
}
//# sourceMappingURL=measurements.js.map